import React, { useRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import Button from "./Button";

const { colors, typography, globals } = tokens;

const StyledField = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-px"],
    minWidth: 0,
});

const StyledLabel = styled.span({
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "20px",
    color: colors.text.primary,
});

const StyledControl = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-3"],
    minWidth: 0,
});

const StyledFile = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    minWidth: 0,
});

const StyledFileLink = styled.a({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    minWidth: 0,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: "20px",
    color: colors.text.link,

    span: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});

const StyledDeleteButton = styled.button({
    padding: 0,
    border: 0,
    background: "none",
    fontSize: typography.fontSize["lg-14px"],
    color: colors.ui.destructive,
    cursor: "pointer",
});

/**
 * Labelled "Upload" button with a hidden file input, plus the attached file's link and delete icon.
 * @param {string} props.label Field label above the button.
 * @param {(file: File) => void} props.onFileSelect Receives the picked file.
 * @param {string} [props.fileName] Attached file's display name; hides the file row when empty.
 * @param {string} [props.fileHref] Link for the attached file; omit for a not-yet-uploaded File.
 * @param {() => void} [props.onDelete] Removes the attached file.
 * Also: buttonText ("Upload"), disabled, error, accept, className.
 */
function FileUploadField({ label, onFileSelect, fileName, fileHref, onDelete, buttonText = "Upload", disabled = false, error = false, accept, className }) {
    const inputRef = useRef(null);

    return (
        <StyledField className={className}>
            <StyledLabel>{label}</StyledLabel>
            <StyledControl>
                <Button
                    type="button"
                    variant="accent"
                    size="md"
                    disabled={disabled}
                    error={error}
                    onClick={() => inputRef.current?.click()}
                >
                    <i className="fal fa-paperclip" aria-hidden="true" />
                    {buttonText}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    disabled={disabled}
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) onFileSelect(file);
                    }}
                />

                {fileName && (
                    <StyledFile>
                        <StyledFileLink href={fileHref} title={fileName} target="_blank" rel="noreferrer">
                            <i className="fal fa-file-alt" aria-hidden="true" />
                            <span>{fileName}</span>
                        </StyledFileLink>
                        {onDelete && (
                            <StyledDeleteButton type="button" title="Delete" onClick={onDelete}>
                                <i className="fal fa-trash-alt" aria-hidden="true" />
                            </StyledDeleteButton>
                        )}
                    </StyledFile>
                )}
            </StyledControl>
        </StyledField>
    );
}

export default FileUploadField;
